import type { RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { buildCaselawStandaloneViewerURL } from '@/standalone-reference-viewer/caselaw/utils/build-caselaw-viewer-url'
import { buildLrrStandaloneViewerURL } from '@/standalone-reference-viewer/lrr/utils/build-lrr-viewer-url'
import { buildUserFileStandaloneViewerURL } from '@/standalone-reference-viewer/user-file/utils/build-user-file-viewer-url'
import { ViewerDocumentKind, ViewerOpenContextKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import type { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { matterPaths } from '@/routing/paths'

export function resolveStandaloneViewerUrlFromSession(
  session: ContentViewerSession | null | undefined,
  context: RiaSourceContext | undefined,
  // Deliberately NOT read from session.presentation: the header's open-in-new-tab
  // pop-out resolves the active session, whose presentation reflects how the panel
  // was opened — a badge-opened panel would leak tab=citator into a source-reading
  // pop-out. Tab intent is per-call, stated by the caller that means it.
  options?: { defaultTab?: CaseLawReferenceTab; defaultOpenSection?: RiaCitatorSection }
): string | undefined {
  const citationContext = session?.context.kind === ViewerOpenContextKind.CHAT_CITATION ? session.context : null
  if (!session || !citationContext) return undefined

  switch (session.document.kind) {
    case ViewerDocumentKind.AGENCY:
      return context?.matterId && session.document.conversationId
        ? matterPaths.referenceViewer.agency(context.matterId, session.document.conversationId, session.document.referenceId)
        : undefined
    case ViewerDocumentKind.CASELAW:
      return (
        buildCaselawStandaloneViewerURL(
          { s3_dir: session.document.s3Dir, s3_path: session.document.s3Path },
          {
            conversationId: citationContext.conversationId,
            messageId: citationContext.turnId,
            matterId: context?.matterId,
            tab: options?.defaultTab,
            section: options?.defaultOpenSection,
          }
        ) || undefined
      )
    case ViewerDocumentKind.LRR:
      return (
        buildLrrStandaloneViewerURL(
          { s3_path: session.document.s3Path },
          {
            matterId: context?.matterId,
            referenceId: citationContext.referenceId,
          }
        ) ?? undefined
      )
    case ViewerDocumentKind.USER_FILE:
      return (
        buildUserFileStandaloneViewerURL(session.document.userFileId, {
          matterId: context?.matterId,
          startingPage: session.presentation?.startingPage,
        }) ?? undefined
      )
    // No standalone route serves a converted PDF: the user-file route would open the original
    // upload, whose pages the citation's highlight geometry does not describe. In-panel only.
    case ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF:
      return undefined
    // No standalone page shows a docket's saved record yet, so a docket opens only in the panel.
    // Adding one also means removing the DOCKET check in canOpenAgentV2Reference (agent-v2-reference-opening.ts).
    case ViewerDocumentKind.DOCKET:
      return undefined
  }
}
